import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  LinearProgress,
  IconButton,
  Paper,
} from "@mui/material";
import { CloudUpload, Delete, VideoFile } from "@mui/icons-material";
import { Module, Program } from "../../store/slices/types";

interface ModuleFormProps {
  module?: Module | null;
  onSave: (moduleData: Partial<Module>, videoFile?: File) => void;
  onCancel: () => void;
  saving?: boolean;
  uploadProgress?: number;
  programs: Program[];
}

const ModuleForm: React.FC<ModuleFormProps> = ({
  module,
  onSave,
  onCancel,
  saving = false,
  uploadProgress = 0,
  programs,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(
    module?.videoUrl || null
  );
  const [isExistingVideo, setIsExistingVideo] = useState<boolean>(
    !!module?.videoUrl
  );

  const [formData, setFormData] = useState({
    title: module?.title || "",
    difficulty: module?.difficulty || "beginner",
    tags: module?.tags?.join(", ") || "",
    content: module?.content || "",
    videoUrl: module?.videoUrl || "",
    programId: module?.programId || "",
    weekNumber: module?.weekNumber || 1,
    order: module?.order || 1,
  });

  useEffect(() => {
    setFormData({
      title: module?.title || "",
      difficulty: module?.difficulty || "beginner",
      tags: module?.tags?.join(", ") || "",
      content: module?.content || "",
      videoUrl: module?.videoUrl || "",
      programId: module?.programId || "",
      weekNumber: module?.weekNumber || 1,
      order: module?.order || 1,
    });
    
    // Clean up previous object URL if it was a newly selected file
    const prevPreview = videoPreview;
    const prevIsExisting = isExistingVideo;
    
    setVideoPreview(module?.videoUrl || null);
    setIsExistingVideo(!!module?.videoUrl);
    setSelectedVideo(null);
    setErrors({});
    
    // Clean up old object URL after state update
    if (prevPreview && !prevIsExisting && prevPreview !== (module?.videoUrl || null)) {
      URL.revokeObjectURL(prevPreview);
    }
  }, [module]);

  // Cleanup object URL on unmount or when video changes
  useEffect(() => {
    return () => {
      if (videoPreview && !isExistingVideo && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview, isExistingVideo]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("video/")) {
        setErrors({ ...errors, videoUrl: "Please select a valid video file" });
        return;
      }

      // Validate file size (e.g., max 100MB for mobile compatibility)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        setErrors({
          ...errors,
          videoUrl: "Video file size must be less than 100MB",
        });
        return;
      }

      // Clean up previous object URL if it exists
      if (videoPreview && !isExistingVideo && videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview);
      }
      
      setSelectedVideo(file);
      setVideoPreview(URL.createObjectURL(file));
      setIsExistingVideo(false);
      if (errors.videoUrl) {
        setErrors({ ...errors, videoUrl: "" });
      }
    }
  };

  const handleRemoveVideo = () => {
    // Clean up object URL if it was created for a new file
    if (selectedVideo && videoPreview && !isExistingVideo) {
      URL.revokeObjectURL(videoPreview);
    }
    setSelectedVideo(null);
    setVideoPreview(null);
    setIsExistingVideo(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFormData({ ...formData, videoUrl: "" });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Module title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Module title must be at least 3 characters";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Text content is required";
    } else if (formData.content.trim().length < 10) {
      newErrors.content = "Text content must be at least 10 characters";
    }

    // Check for video file or existing video URL
    if (!selectedVideo && !formData.videoUrl && !module?.videoUrl) {
      newErrors.videoUrl = "Please upload a video file";
    }

    if (!formData.programId) {
      newErrors.programId = "Please select a program";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const moduleData = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((tag: string) => tag.trim())
          .filter((tag: string) => tag),
        // Only include videoUrl if no new file is selected
        videoUrl: selectedVideo ? "" : formData.videoUrl,
      };
      onSave(moduleData, selectedVideo || undefined);
    }
  };

  return (
    <Box>
      {/* Basic Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📝 Basic Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Module Title'
              placeholder='Enter a descriptive title for your module'
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: "" });
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Difficulty Level</InputLabel>
              <Select
                label='Difficulty Level'
                value={formData.difficulty}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    difficulty: e.target.value as any,
                  })
                }
                sx={{
                  borderRadius: 2,
                  minHeight: 64,
                }}
              >
                <MenuItem value='beginner'>🟢 Beginner</MenuItem>
                <MenuItem value='intermediate'>🟡 Intermediate</MenuItem>
                <MenuItem value='advanced'>🔴 Advanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Program</InputLabel>
              <Select
                label='Program'
                value={formData.programId}
                onChange={(e) => {
                  setFormData({ ...formData, programId: e.target.value });
                  if (errors.programId) setErrors({ ...errors, programId: "" });
                }}
                error={!!errors.programId}
                sx={{
                  borderRadius: 2,
                  minHeight: 64,
                }}
              >
                {programs.map((program) => (
                  <MenuItem key={program._id || program.id} value={program._id || program.id}>
                    {program.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Week Number'
              value={formData.weekNumber || ""}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                setFormData({ ...formData, weekNumber: value });
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Order'
              value={formData.order || ""}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                setFormData({ ...formData, order: value });
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Content Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📄 Content
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label='Text Content'
              placeholder='Enter the text content for this module'
              value={formData.content}
              onChange={(e) => {
                setFormData({ ...formData, content: e.target.value });
                if (errors.content) setErrors({ ...errors, content: "" });
              }}
              error={!!errors.content}
              helperText={errors.content}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 150,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontWeight: 500, color: "text.secondary" }}
                >
                  Video Upload
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  Supported formats: <strong>MP4</strong>, <strong>WebM</strong>, <strong>MOV</strong> • Max size: <strong>100MB</strong>
                </Typography>
              </Box>
              
              {videoPreview ? (
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    mb: 2,
                    bgcolor: "grey.50",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedVideo
                          ? selectedVideo.name
                          : "Existing video"}
                      </Typography>
                      {selectedVideo && (
                        <Typography variant="caption" color="text.secondary">
                          {(selectedVideo.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      onClick={handleRemoveVideo}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                  
                  {/* Video Preview */}
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: 400,
                      margin: "0 auto",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "black",
                      aspectRatio: "16/9",
                    }}
                  >
                    <video
                      src={videoPreview}
                      controls
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        console.error("Video preview error:", e);
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </Box>
                </Paper>
              ) : null}

              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoSelect}
                style={{ display: "none" }}
                id="video-upload-input"
              />
              <label htmlFor="video-upload-input">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    py: 2,
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: errors.videoUrl
                      ? "error.main"
                      : "primary.main",
                    "&:hover": {
                      borderStyle: "dashed",
                      borderWidth: 2,
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {videoPreview
                    ? "Replace Video"
                    : "Upload Video File"}
                </Button>
              </label>
              
              {errors.videoUrl && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {errors.videoUrl}
                </Typography>
              )}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Uploading video...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(uploadProgress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "grey.200",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Tags'
              placeholder='module, learning, wellness'
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
              helperText='Separate tags with commas'
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={onCancel}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
            },
          }}
        >
          {saving
            ? "Saving..."
            : module
            ? "Update Module"
            : "Create Module"}
        </Button>
      </Box>
    </Box>
  );
};

export default ModuleForm;

